// Utilitário de Validações Pré-Pagamento
// Baseado na Suite de Testes Pagar.me

export interface ValidationResult {
  valid: boolean;
  message: string;
  type: 'error' | 'warning' | 'info';
  code?: string;
}

export interface CardValidationResult {
  valid: boolean;
  errors: ValidationResult[];
  warnings: ValidationResult[];
  testCardInfo?: TestCardInfo;
}

export interface TestCardInfo {
  cardNumber: string;
  name: string;
  expectedBehavior: string;
  icon: string;
  color: string;
  isTestCard: boolean;
}

// Cartões de teste da Pagar.me (baseados na Suite de Testes)
const TEST_CARDS: Record<string, TestCardInfo> = {
  '4000000000000010': {
    cardNumber: '4000000000000010',
    name: 'Cartão de Sucesso',
    expectedBehavior: 'Status: Pago, Cobrança: Pago, Transação: Capturado',
    icon: '✅',
    color: 'green',
    isTestCard: true
  },
  '4000000000000028': {
    cardNumber: '4000000000000028',
    name: 'Cartão de Falha',
    expectedBehavior: 'Status: Falha, Cobrança: Falha, Transação: Não Autorizada',
    icon: '❌',
    color: 'red',
    isTestCard: true
  },
  '4000000000000036': {
    cardNumber: '4000000000000036',
    name: 'Processing → Sucesso',
    expectedBehavior: 'Inicial: Pendente/Com erro → Final: Pago/Capturado',
    icon: '⏳',
    color: 'blue',
    isTestCard: true
  },
  '4000000000000044': {
    cardNumber: '4000000000000044',
    name: 'Processing → Falha',
    expectedBehavior: 'Inicial: Pendente/Com erro → Final: Falha',
    icon: '⚠️',
    color: 'orange',
    isTestCard: true
  },
  '4000000000000077': {
    cardNumber: '4000000000000077',
    name: 'Sucesso → Processing → Sucesso',
    expectedBehavior: 'Pago → Processing → Cancelado/Estornado',
    icon: '🔄',
    color: 'purple',
    isTestCard: true
  },
  '4000000000000093': {
    cardNumber: '4000000000000093',
    name: 'Sucesso → Processing → Primeira Op.',
    expectedBehavior: 'Pago → Processing → Pago (primeira operação)',
    icon: '🔄',
    color: 'indigo',
    isTestCard: true
  },
  '4000000000000051': {
    cardNumber: '4000000000000051',
    name: 'Processing → Cancelado',
    expectedBehavior: 'Pendente/Com erro → Cancelado/Estornado',
    icon: '❌',
    color: 'gray',
    isTestCard: true
  },
  '4000000000000069': {
    cardNumber: '4000000000000069',
    name: 'Paid → Chargeback',
    expectedBehavior: 'Pago/Capturado → Chargeback',
    icon: '⚠️',
    color: 'yellow',
    isTestCard: true
  }
};

export class PaymentValidator {
  
  /**
   * Valida número do cartão usando algoritmo de Luhn
   */
  static validateCardNumber(cardNumber: string): ValidationResult {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (!cleanNumber) {
      return {
        valid: false,
        message: 'Número do cartão é obrigatório',
        type: 'error',
        code: 'CARD_NUMBER_REQUIRED'
      };
    }
    
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      return {
        valid: false,
        message: 'Número do cartão deve ter entre 13 e 19 dígitos',
        type: 'error',
        code: 'CARD_NUMBER_LENGTH'
      };
    }
    
    // Algoritmo de Luhn
    let sum = 0;
    let isEven = false;
    
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber[i]);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    const isValid = sum % 10 === 0;
    
    return {
      valid: isValid,
      message: isValid ? 'Número do cartão válido' : 'Número do cartão inválido',
      type: isValid ? 'info' : 'error',
      code: isValid ? 'CARD_NUMBER_VALID' : 'CARD_NUMBER_INVALID'
    };
  }
  
  /**
   * Valida data de expiração
   */
  static validateExpirationDate(month: string, year: string): ValidationResult {
    if (!month || !year) {
      return {
        valid: false,
        message: 'Mês e ano de expiração são obrigatórios',
        type: 'error',
        code: 'EXPIRATION_REQUIRED'
      };
    }
    
    const expMonth = parseInt(month);
    const expYear = parseInt(year);
    
    if (expMonth < 1 || expMonth > 12) {
      return {
        valid: false,
        message: 'Mês de expiração inválido',
        type: 'error',
        code: 'EXPIRATION_MONTH_INVALID'
      };
    }
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    // Ajustar ano se for formato de 2 dígitos
    const fullYear = expYear < 100 ? 2000 + expYear : expYear;
    
    if (fullYear < currentYear || (fullYear === currentYear && expMonth < currentMonth)) {
      return {
        valid: false,
        message: 'Cartão expirado',
        type: 'error',
        code: 'CARD_EXPIRED'
      };
    }
    
    return {
      valid: true,
      message: 'Data de expiração válida',
      type: 'info',
      code: 'EXPIRATION_VALID'
    };
  }
  
  /**
   * Valida CVV
   */
  static validateCVV(cvv: string, cardNumber?: string): ValidationResult {
    if (!cvv) {
      return {
        valid: false,
        message: 'CVV é obrigatório',
        type: 'error',
        code: 'CVV_REQUIRED'
      };
    }
    
    const cleanCVV = cvv.replace(/\D/g, '');
    
    // American Express tem CVV de 4 dígitos
    const isAmex = cardNumber && cardNumber.replace(/\D/g, '').startsWith('3');
    const expectedLength = isAmex ? 4 : 3;
    
    if (cleanCVV.length !== expectedLength) {
      return {
        valid: false,
        message: `CVV deve ter ${expectedLength} dígitos`,
        type: 'error',
        code: 'CVV_LENGTH'
      };
    }
    
    return {
      valid: true,
      message: 'CVV válido',
      type: 'info',
      code: 'CVV_VALID'
    };
  }
  
  /**
   * Valida nome do portador
   */
  static validateHolderName(name: string): ValidationResult {
    if (!name || name.trim().length === 0) {
      return {
        valid: false,
        message: 'Nome do portador é obrigatório',
        type: 'error',
        code: 'HOLDER_NAME_REQUIRED'
      };
    }
    
    if (name.trim().length < 2) {
      return {
        valid: false,
        message: 'Nome do portador deve ter pelo menos 2 caracteres',
        type: 'error',
        code: 'HOLDER_NAME_TOO_SHORT'
      };
    }
    
    // Verificar se contém pelo menos um espaço (nome e sobrenome)
    if (!name.trim().includes(' ')) {
      return {
        valid: false,
        message: 'Informe nome e sobrenome',
        type: 'warning',
        code: 'HOLDER_NAME_INCOMPLETE'
      };
    }
    
    return {
      valid: true,
      message: 'Nome do portador válido',
      type: 'info',
      code: 'HOLDER_NAME_VALID'
    };
  }
  
  /**
   * Valida CPF/CNPJ (Brasil / Estrangeiro Residente) ou Passaporte (estrangeiro não-residente)
   * @param document Documento (CPF/CNPJ ou passaporte)
   * @param codigoIbgePais Código IBGE do país. Brasil = 76 ou 1058.
   * @param tipoPessoa Tipo de pessoa cadastral: 'F'=Brasileiro PF, 'J'=Brasileiro PJ, 'E'=Estrangeiro Residente, 'I'=Estrangeiro NÃO Residente (passaporte). Quando informado, tem precedência sobre codigoIbgePais.
   */
  // {|INICIO| 07/10/2026 às 11:00 - PASSAPORTE: adicionar suporte a documento estrangeiro para cartão internacional}
  // {|INICIO| 07/10/2026 às 15:10 - PASSAPORTE: considerar também tipo_pessoa='I' (precedência máxima sobre IBGE)}
  static validateDocument(
    document: string,
    codigoIbgePais?: number | string | null,
    tipoPessoa?: string | null
  ): ValidationResult {
    if (!document) {
      return {
        valid: false,
        message: 'Documento é obrigatório',
        type: 'error',
        code: 'DOCUMENT_REQUIRED'
      };
    }

    const tipoPessoaNormalizado = tipoPessoa ? String(tipoPessoa).toUpperCase().trim() : null;

    if (tipoPessoaNormalizado === 'I') {
      return this.validatePassaporte(document);
    }

    if (tipoPessoaNormalizado === 'F' || tipoPessoaNormalizado === 'J' || tipoPessoaNormalizado === 'E') {
      return this._validateCpfCnpj(document);
    }

    const codigoIbgePaisNormalizado = codigoIbgePais != null ? Number(codigoIbgePais) : null;
    const clienteEhBrasil =
      codigoIbgePaisNormalizado == null ||
      Number.isNaN(codigoIbgePaisNormalizado) ||
      codigoIbgePaisNormalizado === 76 ||
      codigoIbgePaisNormalizado === 1058;

    if (!clienteEhBrasil) {
      return this.validatePassaporte(document);
    }

    return this._validateCpfCnpj(document);
  }

  /**
   * Validação interna de CPF/CNPJ (11 ou 14 dígitos com dígitos verificadores).
   * Usado tanto para clientes Brasileiros (F/J) como Estrangeiros Residentes (E).
   */
  private static _validateCpfCnpj(document: string): ValidationResult {
    const documentoSomenteDigitos = document.replace(/\D/g, '');

    if (documentoSomenteDigitos.length !== 11 && documentoSomenteDigitos.length !== 14) {
      return {
        valid: false,
        message: 'Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos',
        type: 'error',
        code: 'DOCUMENT_LENGTH'
      };
    }

    if ((documentoSomenteDigitos.length === 11 && /^(\d)\1{10}$/.test(documentoSomenteDigitos)) || (documentoSomenteDigitos.length === 14 && /^(\d)\1{13}$/.test(documentoSomenteDigitos))) {
      return {
        valid: false,
        message: documentoSomenteDigitos.length === 11 ? 'CPF inválido' : 'CNPJ inválido',
        type: 'error',
        code: 'DOCUMENT_INVALID'
      };
    }

    if (documentoSomenteDigitos.length === 11) {
      let soma = 0;
      for (let i = 0; i < 9; i++) {
        soma += parseInt(documentoSomenteDigitos[i]) * (10 - i);
      }
      let digito1 = 11 - (soma % 11);
      if (digito1 > 9) digito1 = 0;

      soma = 0;
      for (let i = 0; i < 10; i++) {
        soma += parseInt(documentoSomenteDigitos[i]) * (11 - i);
      }
      let digito2 = 11 - (soma % 11);
      if (digito2 > 9) digito2 = 0;

      if (parseInt(documentoSomenteDigitos[9]) !== digito1 || parseInt(documentoSomenteDigitos[10]) !== digito2) {
        return {
          valid: false,
          message: 'CPF inválido',
          type: 'error',
          code: 'DOCUMENT_INVALID'
        };
      }

      return {
        valid: true,
        message: 'CPF válido',
        type: 'info',
        code: 'DOCUMENT_VALID'
      };
    }

    const pesosPrimeiroDigito = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const pesosSegundoDigito = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const calcularDigito = (base: string, pesos: number[]): number => {
      let soma = 0;
      for (let i = 0; i < pesos.length; i++) {
        soma += parseInt(base[i]) * pesos[i];
      }
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const base12 = documentoSomenteDigitos.slice(0, 12);
    const digito1 = calcularDigito(base12, pesosPrimeiroDigito);
    const base13 = `${base12}${digito1}`;
    const digito2 = calcularDigito(base13, pesosSegundoDigito);

    if (parseInt(documentoSomenteDigitos[12]) !== digito1 || parseInt(documentoSomenteDigitos[13]) !== digito2) {
      return {
        valid: false,
        message: 'CNPJ inválido',
        type: 'error',
        code: 'DOCUMENT_INVALID'
      };
    }

    return {
      valid: true,
      message: 'CNPJ válido',
      type: 'info',
      code: 'DOCUMENT_VALID'
    };
  }
  // {|FIM| 07/10/2026 às 15:10 - PASSAPORTE: considerar também tipo_pessoa='I' (precedência máxima sobre IBGE)}
  // {|FIM| 07/10/2026 às 11:00 - PASSAPORTE: adicionar suporte a documento estrangeiro para cartão internacional}

  /**
   * Valida número de passaporte (alfanumérico, 4 a 20 caracteres, sem símbolos)
   */
  static validatePassaporte(passaporte: string): ValidationResult {
    if (!passaporte) {
      return {
        valid: false,
        message: 'Passaporte é obrigatório',
        type: 'error',
        code: 'PASSPORT_REQUIRED'
      };
    }

    const passaporteLimpo = passaporte.trim();

    if (passaporteLimpo.length < 4 || passaporteLimpo.length > 20) {
      return {
        valid: false,
        message: 'Passaporte deve ter entre 4 e 20 caracteres alfanuméricos',
        type: 'error',
        code: 'PASSPORT_LENGTH'
      };
    }

    if (!/^[A-Za-z0-9]+$/.test(passaporteLimpo)) {
      return {
        valid: false,
        message: 'Passaporte deve conter apenas letras e números',
        type: 'error',
        code: 'PASSPORT_FORMAT'
      };
    }

    return {
      valid: true,
      message: 'Passaporte válido',
      type: 'info',
      code: 'PASSPORT_VALID'
    };
  }

  /**
   * Indica se cliente é estrangeiro não-residente e, portanto, deve usar passaporte em vez de CPF/CNPJ.
   * Regra de precedência: tipo_pessoa > código IBGE (garante consistência com tela Meu Perfil / Cadastro).
   * @param codigoIbgePais Código IBGE do país (76 e 1058 = Brasil)
   * @param tipoPessoa Tipo cadastral: 'F'|'J' = BR, 'E' = residente (CPF), 'I' = NÃO residente (Passaporte)
   */
  // {|INICIO| 07/10/2026 às 15:15 - PASSAPORTE: helper deve considerar tipo_pessoa='I' como fonte da verdade}
  static clienteEhEstrangeiroNaoResidente(
    codigoIbgePais?: number | string | null,
    tipoPessoa?: string | null
  ): boolean {
    const tipoPessoaNormalizado = tipoPessoa ? String(tipoPessoa).toUpperCase().trim() : null;
    if (tipoPessoaNormalizado === 'I') return true;
    if (tipoPessoaNormalizado === 'F' || tipoPessoaNormalizado === 'J' || tipoPessoaNormalizado === 'E') return false;

    const codigoIbgePaisNormalizado = codigoIbgePais != null ? Number(codigoIbgePais) : null;
    if (codigoIbgePaisNormalizado == null || Number.isNaN(codigoIbgePaisNormalizado)) return false;
    return codigoIbgePaisNormalizado !== 76 && codigoIbgePaisNormalizado !== 1058;
  }
  // {|FIM| 07/10/2026 às 15:15 - PASSAPORTE: helper deve considerar tipo_pessoa='I' como fonte da verdade}
  
  /**
   * Identifica se é cartão de teste
   */
  static getTestCardInfo(cardNumber: string): TestCardInfo | undefined {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    return TEST_CARDS[cleanNumber];
  }
  
  /**
   * Validação completa do cartão
   * @param cardData Dados do cartão
   * @param options.codigoIbgePais Código IBGE do país para decidir validação de documento (Brasil=CPF/CNPJ, estrangeiro=passaporte)
   * @param options.tipoPessoa Tipo cadastral ('F'/'J'/'E' = CPF; 'I' = Passaporte) — precedência máxima sobre codigoIbgePais
   */
  static validateCard(
    cardData: {
      number: string;
      holder_name: string;
      exp_month: string;
      exp_year: string;
      cvv: string;
      holder_document: string;
    },
    options?: { codigoIbgePais?: number | string | null; tipoPessoa?: string | null }
  ): CardValidationResult {
    const errors: ValidationResult[] = [];
    const warnings: ValidationResult[] = [];

    // Validar cada campo
    const validations = [
      this.validateCardNumber(cardData.number),
      this.validateExpirationDate(cardData.exp_month, cardData.exp_year),
      this.validateCVV(cardData.cvv, cardData.number),
      this.validateHolderName(cardData.holder_name),
      this.validateDocument(cardData.holder_document, options?.codigoIbgePais, options?.tipoPessoa)
    ];
    
    validations.forEach(validation => {
      if (!validation.valid) {
        if (validation.type === 'error') {
          errors.push(validation);
        } else if (validation.type === 'warning') {
          warnings.push(validation);
        }
      }
    });
    
    // Verificar se é cartão de teste
    const testCardInfo = this.getTestCardInfo(cardData.number);
    if (testCardInfo) {
      warnings.push({
        valid: true,
        message: `Cartão de teste detectado: ${testCardInfo.name}`,
        type: 'warning',
        code: 'TEST_CARD_DETECTED'
      });
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings,
      testCardInfo
    };
  }
}
